import type { DailyNutritionSummary } from './mealLogCore'

export type CalendarStressContext = {
  calendar_connected: boolean
  cognitive_load_mode: 'standard' | 'calendar_informed'
  has_historical_events: boolean
  correlations_found: boolean
  correlations: {
    event_keyword: string
    sample_count: number
    avg_next_day_hrv_impact_pct: number | null
    avg_next_day_resting_hr_delta: number | null
  }[]
  educational_message: string | null
  unlock_hint: string | null
}

export type CoachHealthSummary = {
  today: Record<string, unknown>
  week_averages: Record<string, unknown>
  recovery_debt: Record<string, unknown>
  todays_briefing: Record<string, unknown> | null
  visible_metrics: string[]
  connected_providers: string[]
  has_device_connected: boolean
  has_metric_data?: boolean
  metrics_synced?: number
  calendar_stress_context?: CalendarStressContext
  cognitive_load_baseline?: 'standard' | 'calendar_informed'
  nutrition_yesterday?: DailyNutritionSummary | null
  nutrition_coach_note?: string | null
  nutrition_first_day?: boolean
  physiological_day?: string
}

export type MetricCardId =
  | 'hrv'
  | 'resting_heart_rate'
  | 'sleep_duration'
  | 'recovery_score'
  | 'active_calories'
  | 'steps'
  | 'vo2_max'
  | 'weight'
  | 'body_fat'
  | 'stress_level'
  | 'workout_strain'

export const METRIC_CARD_CATALOGUE: Array<{
  id: MetricCardId
  label: string
  unit?: string
  coach_uses: boolean
  trendGood: 'up' | 'down' | null
}> = [
  { id: 'hrv', label: 'HRV', unit: 'ms', coach_uses: true, trendGood: 'up' as const },
  { id: 'resting_heart_rate', label: 'Resting HR', unit: 'bpm', coach_uses: true, trendGood: 'down' as const },
  { id: 'sleep_duration', label: 'Sleep', unit: 'hrs', coach_uses: true, trendGood: 'up' as const },
  { id: 'recovery_score', label: 'Recovery', unit: '/100', coach_uses: true, trendGood: 'up' as const },
  { id: 'active_calories', label: 'Active Cal', unit: 'kcal', coach_uses: true, trendGood: 'up' as const },
  { id: 'steps', label: 'Steps', coach_uses: false, trendGood: 'up' as const },
  { id: 'vo2_max', label: 'VO2 Max', unit: 'ml/kg', coach_uses: false, trendGood: 'up' as const },
  { id: 'weight', label: 'Weight', unit: 'kg', coach_uses: false, trendGood: null },
  { id: 'body_fat', label: 'Body Fat', unit: '%', coach_uses: false, trendGood: 'down' as const },
  { id: 'stress_level', label: 'Stress', unit: '/100', coach_uses: false, trendGood: 'down' as const },
  { id: 'workout_strain', label: 'Workout Strain', coach_uses: false, trendGood: 'down' as const },
]

export function getMetricTrend(todayVal: number | null, avgVal: number | null): 'up' | 'down' | 'stable' | null {
  if (todayVal == null || avgVal == null || avgVal === 0) return null
  const pct = ((todayVal - avgVal) / avgVal) * 100
  if (pct > 5) return 'up'
  if (pct < -5) return 'down'
  return 'stable'
}
